import { useTranslation } from 'react-i18next';
import { FileText, Target, Link2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const HowTo = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const steps = t('howto.steps', { returnObjects: true }) as any[];
  const icons = [FileText, Target, Link2];
  
  // 写真のパス（publicフォルダから読み込み）
  const stepImages = [
    '/step_new.jpg',
    '/tesp2.gif.jpg',
    '/step3.gif.jpg'
  ];
  
  // デバッグ用：画像パスをログ出力
  console.log('Step images:', stepImages);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2 
          className="text-4xl md:text-5xl lg:text-6xl font-black text-center text-gray-900 mb-20 tracking-tight"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {t('howto.title')}
        </motion.h2>

        <motion.div 
          ref={ref}
          className="grid md:grid-cols-3 gap-12"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {steps.map((step: any, index: number) => {
            const Icon = icons[index];
            return (
              <motion.div
                key={index}
                className="relative bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all hover:scale-105 border-2 border-blue-200 overflow-hidden"
                variants={itemVariants}
                whileHover={{ scale: 1.05, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {/* 写真エリア（上部） */}
                <div className="relative w-full h-64 md:h-80 bg-gradient-to-br from-blue-100 to-cyan-100">
                  {/* 背景写真 */}
                  <img 
                    src={stepImages[index]}
                    alt={step.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // 画像が読み込めない場合はグラデーション背景を表示
                      console.error(`Failed to load image: ${stepImages[index]}`, e);
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  {/* ステップ番号（写真の上に重ねる） */}
                  <div className="absolute top-4 right-4 bg-gradient-to-br from-blue-500 to-cyan-500 text-white font-black text-2xl w-16 h-16 rounded-full flex items-center justify-center shadow-lg border-4 border-white">
                    {index + 1}
                  </div>
                </div>
                
                {/* コンテンツエリア */}
                <div className="p-8 text-center">
                  <div className="mb-6">
                    <div className="inline-block bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full p-4 mb-4 shadow-lg">
                      <Icon size={32} className="text-white" />
                    </div>
                  </div>
                  <h3 className="text-3xl font-black text-gray-900 mb-4">
                    {step.title}
                  </h3>
                  <p className="text-xl text-gray-700 leading-relaxed font-semibold text-center mb-4">
                    {step.description}
                  </p>
                  {index === 0 && (
                    <div className="mt-4 text-sm text-gray-500 flex flex-wrap justify-center gap-2">
                      <span className="px-3 py-1 bg-gray-100 rounded-full">FAQ</span>
                      <span className="px-3 py-1 bg-gray-100 rounded-full">マニュアル</span>
                      <span className="px-3 py-1 bg-gray-100 rounded-full">メールの履歴</span>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div 
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
        >
          <p className="text-2xl font-bold text-gray-600">
            {t('howto.footer')}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default HowTo;

